import React, { useState } from 'react';

// Define types for our component
interface CsvToTextProps {}

// CSV to TEXT conversion function
const csvToText = (
  csv: string, 
  options: {
    header: boolean;
    delimiter: string;
    outputDelimiter: string;
    trimValues: boolean;
    skipEmptyLines: boolean;
    includeRowNumbers: boolean;
    alignColumns: boolean;
  }
): string => {
  try {
    // Split the CSV by lines
    const lines = csv.split(/\r?\n/);
    
    // Handle empty CSV
    if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
      return '';
    }
    
    let startRow = 0;
    let headers: string[] = [];
    let columnWidths: number[] = [];
    
    // Process headers if they exist
    if (options.header && lines.length > 0) {
      const headerRow = lines[0];
      headers = parseCSVLine(headerRow, options.delimiter);
      if (options.trimValues) {
        headers = headers.map(header => header.trim());
      }
      startRow = 1;
      
      // Initialize column widths with header lengths if we're aligning columns
      if (options.alignColumns) {
        columnWidths = headers.map(header => header.length);
      }
    }
    
    // Pre-process all data rows to determine column widths if aligning
    if (options.alignColumns) {
      for (let i = startRow; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Skip empty lines if configured to do so
        if (options.skipEmptyLines && line === '') {
          continue;
        }
        
        const values = parseCSVLine(line, options.delimiter);
        
        // Update column widths
        values.forEach((value, j) => {
          const trimmedValue = options.trimValues ? value.trim() : value;
          if (j >= columnWidths.length) {
            columnWidths.push(trimmedValue.length);
          } else {
            columnWidths[j] = Math.max(columnWidths[j], trimmedValue.length);
          }
        });
      }
    }
    
    // Initialize text output
    let textOutput = '';
    
    // Add header to output if applicable
    if (options.header) {
      if (options.includeRowNumbers) {
        textOutput += 'Row' + options.outputDelimiter;
      }
      
      if (options.alignColumns) {
        textOutput += headers.map((header, i) => 
          header.padEnd(columnWidths[i])
        ).join(options.outputDelimiter);
      } else {
        textOutput += headers.join(options.outputDelimiter);
      }
      
      textOutput += '\n';
      
      // Add separator line
      if (options.alignColumns) {
        if (options.includeRowNumbers) {
          const rowNumWidth = 'Row'.length;
          textOutput += '-'.repeat(rowNumWidth) + options.outputDelimiter;
        }
        
        textOutput += columnWidths.map(width => 
          '-'.repeat(width)
        ).join(options.outputDelimiter) + '\n';
      }
    }
    
    // Process data rows
    for (let i = startRow; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines if configured to do so
      if (options.skipEmptyLines && line === '') {
        continue;
      }
      
      const values = parseCSVLine(line, options.delimiter);
      
      if (values.length > 0) {
        if (options.includeRowNumbers) {
          textOutput += `${i - startRow + 1}${options.outputDelimiter}`;
        }
        
        if (options.alignColumns) {
          textOutput += values.map((value, j) => {
            const trimmedValue = options.trimValues ? value.trim() : value;
            return trimmedValue.padEnd(columnWidths[j] || 0);
          }).join(options.outputDelimiter);
        } else {
          const processedValues = options.trimValues 
            ? values.map(v => v.trim()) 
            : values;
          textOutput += processedValues.join(options.outputDelimiter);
        }
        
        textOutput += '\n';
      }
    }
    
    return textOutput;
  } catch (error) {
    throw error;
  }
};

// Helper function to parse a CSV line respecting quoted values
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Handle escaped double quotes
      if (i + 1 < line.length && line[i + 1] === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle the quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  return values;
};

// Main component to convert CSV to Text
const CsvToText: React.FC<CsvToTextProps> = () => {
  // State for the CSV input
  const [csvInput, setCsvInput] = useState<string>('');
  // State for the Text output
  const [textOutput, setTextOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isTextGenerated, setIsTextGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for preview
  const [showPreview, setShowPreview] = useState<boolean>(false);
  
  // Conversion options
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [inputDelimiter, setInputDelimiter] = useState<string>(',');
  const [outputDelimiter, setOutputDelimiter] = useState<string>('\t');
  const [trimValues, setTrimValues] = useState<boolean>(true);
  const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
  const [includeRowNumbers, setIncludeRowNumbers] = useState<boolean>(false);
  const [alignColumns, setAlignColumns] = useState<boolean>(true);
  
  // Handle CSV input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCsvInput(e.target.value);
    setError('');
    setIsTextGenerated(false);
    setShowPreview(false);
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Update the file name based on the uploaded file
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop(); // Remove extension
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }
    
    // Read the file content
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const csv = event.target.result as string;
        setCsvInput(csv);
        setError('');
        setIsTextGenerated(false);
        setShowPreview(false);
      }
    };
    reader.onerror = () => {
      setError('Error reading the file');
    };
    reader.readAsText(file);
  };

  // Use example data
  const useExampleData = () => {
    const exampleCsv = `Product,Category,Price,Stock,Rating
Basic T-Shirt,Clothing,19.99,120,4.2
Premium Hoodie,Clothing,49.99,65,4.8
Wireless Earbuds,Electronics,89.99,42,4.5
Smart Watch,Electronics,199.99,30,4.7
Running Shoes,Footwear,79.99,80,4.3
Yoga Mat,Fitness,29.99,55,4.6
Protein Powder,Nutrition,39.99,100,4.4
Water Bottle,Accessories,14.99,150,4.1`;

    setCsvInput(exampleCsv);
    setError('');
    setIsTextGenerated(false);
    setShowPreview(false);
    setTextOutput('');
  };

  // Convert CSV to Text
  const convertCsvToText = () => {
    setError('');
    setIsLoading(true);
    setIsTextGenerated(false);
    setTextOutput('');
    setShowPreview(false);
    
    if (!csvInput.trim()) {
      setError('Please enter CSV data');
      setIsLoading(false);
      return;
    }

    try {
      // Generate the Text output
      const options = {
        header: hasHeader,
        delimiter: inputDelimiter,
        outputDelimiter: outputDelimiter,
        trimValues,
        skipEmptyLines,
        includeRowNumbers,
        alignColumns
      };
      
      const text = csvToText(csvInput, options);
      setTextOutput(text);
      setIsTextGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting CSV to Text: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setCsvInput('');
    setTextOutput('');
    setError('');
    setFileName('data');
    setIsTextGenerated(false);
    setShowPreview(false);
  };

  // Copy Text to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(textOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Text file
  const downloadText = () => {
    const blob = new Blob([textOutput], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'data'}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Toggle preview
  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  // Get preview content
  const getPreviewContent = (): React.ReactNode => {
    if (!textOutput.trim()) return null;
    
    const lines = textOutput.trim().split('\n');
    const previewLines = lines.slice(0, 20); // Show first 20 lines
    
    return (
      <div>
        {previewLines.map((line, index) => (
          <div key={index} className="text-sm font-mono whitespace-pre overflow-x-auto">
            {line}
          </div>
        ))}
        {lines.length > 20 && (
          <div className="text-sm text-gray-500 mt-2">
            ...and {lines.length - 20} more lines
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CSV to Text (TXT) Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert CSV (Comma-Separated Values) data to plain text (TXT) format. Upload a CSV file or paste your data below.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="csvInput" className="font-medium text-gray-700">
                CSV Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setCsvInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
            
            <textarea
              id="csvInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={csvInput}
              onChange={handleInputChange}
              placeholder="Paste your CSV data here, e.g.,
Product,Category,Price,Stock
T-Shirt,Clothing,19.99,120
Headphones,Electronics,89.99,42"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                Output File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasHeader"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasHeader" className="ml-2 block text-sm text-gray-700">
                    First row contains headers
                  </label>
                </div>
                
                <div>
                  <label htmlFor="inputDelimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Input Delimiter
                  </label>
                  <select
                    id="inputDelimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={inputDelimiter}
                    onChange={(e) => setInputDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="outputDelimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Output Delimiter
                  </label>
                  <select
                    id="outputDelimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={outputDelimiter}
                    onChange={(e) => setOutputDelimiter(e.target.value)}
                  >
                    <option value="\t">Tab</option>
                    <option value=" ">Space</option>
                    <option value="|">Pipe (|)</option>
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value=":">Colon (:)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trimValues"
                    checked={trimValues}
                    onChange={(e) => setTrimValues(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="trimValues" className="ml-2 block text-sm text-gray-700">
                    Trim whitespace from values
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skipEmptyLines"
                    checked={skipEmptyLines}
                    onChange={(e) => setSkipEmptyLines(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="skipEmptyLines" className="ml-2 block text-sm text-gray-700">
                    Skip empty lines
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeRowNumbers"
                    checked={includeRowNumbers}
                    onChange={(e) => setIncludeRowNumbers(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeRowNumbers" className="ml-2 block text-sm text-gray-700">
                    Include row numbers
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="alignColumns"
                    checked={alignColumns}
                    onChange={(e) => setAlignColumns(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="alignColumns" className="ml-2 block text-sm text-gray-700">
                    Align columns with spaces (for monospaced viewing)
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertCsvToText}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Text'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Text Output
            </label>
            
            {isTextGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Text'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadText}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={togglePreview}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isTextGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Text output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isTextGenerated && !showPreview && (
              <div className="flex-grow flex flex-col">                
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="textOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={textOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
            
            {!error && isTextGenerated && showPreview && (
              <div className="flex-grow flex flex-col border border-gray-300 rounded-md p-3 bg-white overflow-auto">
                <div className="prose prose-sm max-w-none">
                  {getPreviewContent()}
                </div>
              </div>
            )}
          </div>
          
          {isTextGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">CSV Successfully Converted to Text!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated text file. The text format is ideal for legacy systems, simple data viewing, or when you need human-readable tabular data without any markup.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About CSV to Text Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">What is Plain Text (.txt) Format?</h3>
            <p className="text-gray-600">
              Plain text (.txt) format is a simple, universal file format that contains unformatted text. It can be opened by virtually any text editor or word processor on any operating system. Plain text files are lightweight, easy to read, and perfect for data that needs to be human-readable or processed by legacy systems.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool parses CSV (Comma-Separated Values) data and converts it into plain text format. You can customize how the output text is formatted with options like column alignment, custom delimiters, and row numbering. This makes it easy to create readable, structured text files from your CSV data that can be viewed in any text editor while preserving the tabular structure.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>File Upload</strong> - Upload CSV files directly or paste content manually</li>
              <li><strong>Delimiter Customization</strong> - Choose different input and output delimiters</li>
              <li><strong>Column Alignment</strong> - Option to align columns for easier reading in monospaced fonts</li>
              <li><strong>Row Numbering</strong> - Add row numbers to the output for reference</li>
              <li><strong>Header Support</strong> - Properly handle and format header rows</li>
              <li><strong>Quote Handling</strong> - Properly handle quoted values in CSV files</li>
              <li><strong>Whitespace Trimming</strong> - Option to trim whitespace from field values</li>
              <li><strong>Empty Line Skipping</strong> - Option to ignore empty lines in the input</li>
              <li><strong>Live Preview</strong> - See how your data will look in text format</li>
              <li><strong>Text Download</strong> - Save your data as a TXT file</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">When to Use TXT Format</h3>
            <p className="text-gray-600">
              Plain text format is ideal for:
            </p>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Viewing data in any text editor without special software</li>
              <li>Creating human-readable reports</li>
              <li>Working with legacy systems that require plain text input</li>
              <li>Simple data interchange between different platforms</li>
              <li>Creating fixed-width or delimited text files for import into other applications</li>
              <li>Generating log files or simple reports</li>
              <li>Email-friendly data sharing (can be included in email body)</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">CSV Format Best Practices</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use consistent delimiter throughout the file</li>
              <li>Include a header row for better structure</li>
              <li>Use double quotes around values that contain delimiters or newlines</li>
              <li>Escape double quotes within quoted fields by doubling them (e.g., "He said ""Hello"" to me")</li>
              <li>Keep column count consistent across all rows</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Plain Text Format Tips</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>For best viewing results, use monospaced fonts (like Courier, Consolas, or Monaco)</li>
              <li>Enable the "Align columns" option for data that needs to be visually aligned</li>
              <li>Use tabs as delimiters for better compatibility with spreadsheet imports</li>
              <li>Include row numbers for easier reference, especially in large datasets</li>
              <li>For fixed-width formats, ensure that column widths accommodate the longest values</li>
              <li>UTF-8 encoding is recommended for international character support</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Working with Text Files</h3>
            <p className="text-gray-600">
              Plain text files (.txt) can be opened and processed with virtually any text editor or programming language:
            </p>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Text Editors</strong>: Notepad, TextEdit, VS Code, Sublime Text, etc.</li>
              <li><strong>Word Processors</strong>: Microsoft Word, Google Docs, LibreOffice Writer, etc.</li>
              <li><strong>Programming</strong>: Can be easily parsed line-by-line in any programming language</li>
              <li><strong>Spreadsheets</strong>: Can be imported into Excel, Google Sheets, etc. (especially with tab delimiters)</li>
              <li><strong>Command Line</strong>: Can be processed with tools like grep, awk, sed, etc.</li>
              <li><strong>Database Import</strong>: Many databases support importing from delimited text files</li>
            </ul>
          </div>
        </div>
    </div>
</div>
  )
}
export default CsvToText;